import { useId } from 'react'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { SEMANTIC_COLORS } from './theme'
import { BBTooltipContent, type ValueFormatter } from './ChartTooltip'

interface SparklineProps {
  data: Array<Record<string, unknown>>
  dataKey: string
  /** Series name shown in the tooltip */
  label?: string
  color?: string
  /** Number of px, or a percentage to fill a sized parent (e.g. a flex-1 band) */
  height?: number | string
  valueFormat?: ValueFormatter
  labelFormat?: (label: string | number) => string
  /** Key used for the tooltip label (e.g. a date field) */
  xKey?: string
  showTooltip?: boolean
}

/**
 * Y-domain tightened to the data's own range (±10% of the range, not of the
 * absolute value) so real movement fills the strip; a high-baseline metric
 * like ARR/sub would otherwise squash into the top of the card. The floor is
 * clamped to 0 for non-negative series so zero days sit on the baseline.
 * Non-number entries (null gaps) are skipped, never coerced to 0.
 */
// eslint-disable-next-line react-refresh/only-export-components -- domain helper shared with TvMetricsChannel
export function tightDomain(values: readonly unknown[]): [number, number] | undefined {
  let min = Infinity
  let max = -Infinity
  for (const value of values) {
    if (typeof value === 'number' && isFinite(value)) {
      min = Math.min(min, value)
      max = Math.max(max, value)
    }
  }
  if (min === Infinity) return undefined
  const range = max - min || Math.abs(max) || 1
  const pad = range * 0.1
  const floor = min >= 0 ? Math.max(0, min - pad) : min - pad
  return [floor, max + pad]
}

/**
 * Axis-less mini area chart for KPI cards and compact rows. Tight y-domain
 * so small movements stay visible; no grid, no axes, optional tooltip.
 */
export function Sparkline({
  data,
  dataKey,
  label,
  color = SEMANTIC_COLORS.brand,
  height = 48,
  valueFormat,
  labelFormat,
  xKey,
  showTooltip = true,
}: SparklineProps) {
  const gradientId = useId()
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {xKey && <XAxis dataKey={xKey} hide />}
        <YAxis hide domain={tightDomain(data.map(row => row[dataKey]))} />
        {showTooltip && (
          <Tooltip
            content={<BBTooltipContent valueFormat={valueFormat} labelFormat={labelFormat} />}
            cursor={{ stroke: color, strokeOpacity: 0.35 }}
            // Above overlaid text layers (e.g. the z-10 KPI-card text shell)
            wrapperStyle={{ zIndex: 20 }}
          />
        )}
        <Area
          type="monotone"
          dataKey={dataKey}
          name={label ?? dataKey}
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={showTooltip ? { r: 2.5 } : false}
          animationDuration={900}
          animationEasing="ease-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
